import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Customers from "@/pages/Customers";
import Materials from "@/pages/Materials";
import NpdList from "@/pages/NpdList";
import NpdForm from "@/pages/NpdForm";
import Tnc from "@/pages/Tnc";
import SettingsPage from "@/pages/Settings";
import RbacAudit from "@/pages/RbacAudit";
import Trash from "@/pages/Trash";
import ProjectList from "@/pages/ProjectList";
import ProjectForm from "@/pages/ProjectForm";
import ProjectDetail from "@/pages/ProjectDetail";
import Units from "@/pages/Units";
import UsersAdmin from "@/pages/UsersAdmin";
import Roles from "@/pages/Roles";
import ApiKeys from "@/pages/ApiKeys";
import Operations from "@/pages/Operations";
import Machines from "@/pages/Machines";
import ApqpMaster from "@/pages/ApqpMaster";
import ApqpReports from "@/pages/ApqpReports";
import Ballooning from "@/pages/Ballooning";
import BallooningViewer from "@/pages/BallooningViewer";
import DrawingExtract from "@/pages/DrawingExtract";
import KcTest from "@/pages/KcTest";
import KcViewerPage from "@/pages/KcViewerPage";
import ApqpProjects from "@/pages/ApqpProjects";
import ApqpProjectDetail from "@/pages/ApqpProjectDetail";
import ApqpReview from "@/pages/ApqpReview";
import NpdPreview from "@/pages/NpdPreview";
import NpdRoundPreview from "@/pages/NpdRoundPreview";
import ProjectPreview from "@/pages/ProjectPreview";
import AssemblyList from "@/pages/AssemblyList";
import CostBreakdown from "@/pages/CostBreakdown";
import AssemblyForm from "@/pages/AssemblyForm";
import AssemblyPreview from "@/pages/AssemblyPreview";
import AssemblyCompare from "@/pages/AssemblyCompare";
import NpdCompare from "@/pages/NpdCompare";
import Reports from "@/pages/Reports";
import RfqList from "@/pages/RfqList";
import RfqDetail from "@/pages/RfqDetail";
import RfqOrphans from "@/pages/RfqOrphans";
import HrExport from "@/pages/HrExport";

function App() {
  // Keep the browser tab title in sync at runtime — overrides any cached index.html
  // so the rename to "AAMPL NPD System" always shows once the app loads.
  useEffect(() => {
    document.title = "AAMPL NPD System";
  }, []);

  // Global guard: mouse-wheel over a focused number input must NOT change its value.
  // Blur it on wheel so the digits stay put and the page scrolls normally instead.
  useEffect(() => {
    const onWheel = (e) => {
      const el = document.activeElement;
      if (el && el.tagName === "INPUT" && el.type === "number" && el === e.target) {
        e.preventDefault(); // stop the value change on this tick…
        el.blur();          // …and drop focus so further scrolling moves the page
      }
    };
    document.addEventListener("wheel", onWheel, { passive: false });
    return () => document.removeEventListener("wheel", onWheel);
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/npd/:id/preview" element={<ProtectedRoute><NpdPreview /></ProtectedRoute>} />
            <Route path="/npd/:id/rounds/:roundId/preview" element={<ProtectedRoute><NpdRoundPreview /></ProtectedRoute>} />
            <Route path="/projects/:id/preview" element={<ProtectedRoute><ProjectPreview /></ProtectedRoute>} />
            <Route path="/assemblies/:id/preview" element={<ProtectedRoute><AssemblyPreview /></ProtectedRoute>} />
            <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="npd" element={<NpdList />} />
              <Route path="npd/new" element={<NpdForm />} />
              <Route path="npd/compare" element={<NpdCompare />} />
              <Route path="npd/:id" element={<NpdForm />} />
              <Route path="rfqs" element={<RfqList />} />
              <Route path="rfqs/:rfqId" element={<RfqDetail />} />
              <Route path="projects" element={<ProjectList />} />
              <Route path="projects/new" element={<ProjectForm />} />
              <Route path="projects/:id/edit" element={<ProjectForm />} />
              <Route path="projects/:id" element={<ProjectDetail />} />
              <Route path="assemblies" element={<AssemblyList />} />
              <Route path="cost-breakdown" element={<CostBreakdown />} />
              <Route path="assemblies/new" element={<AssemblyForm />} />
              <Route path="assemblies/compare" element={<AssemblyCompare />} />
              <Route path="assemblies/:id" element={<AssemblyForm />} />
              <Route path="reports" element={<ProtectedRoute roles={["admin", "npd_manager"]}><Reports /></ProtectedRoute>} />
              <Route path="customers" element={<ProtectedRoute roles={["admin", "npd_manager"]}><Customers /></ProtectedRoute>} />
              <Route path="materials" element={<Materials />} />
              <Route path="operations" element={<Operations />} />
              <Route path="machines" element={<Machines />} />
              <Route path="apqp-master" element={<ProtectedRoute roles={["admin"]}><ApqpMaster /></ProtectedRoute>} />
              <Route path="apqp" element={<ApqpProjects />} />
              <Route path="apqp-reports" element={<ApqpReports />} />
              <Route path="ballooning" element={<Ballooning />} />
              <Route path="ballooning/:id" element={<BallooningViewer />} />
              <Route path="drawing-extract" element={<DrawingExtract />} />
              <Route path="kc-test" element={<KcTest />} />
              <Route path="kc-test/:id" element={<KcViewerPage />} />
              <Route path="apqp/:apqpId" element={<ApqpProjectDetail />} />
              <Route path="apqp/:apqpId/review" element={<ApqpReview />} />
              <Route path="tnc" element={<ProtectedRoute roles={["admin"]}><Tnc /></ProtectedRoute>} />
              <Route path="settings" element={<ProtectedRoute roles={["admin", "npd_manager"]}><SettingsPage /></ProtectedRoute>} />
              <Route path="audit" element={<ProtectedRoute roles={["admin"]}><RbacAudit /></ProtectedRoute>} />
              <Route path="trash" element={<ProtectedRoute roles={["admin"]}><Trash /></ProtectedRoute>} />
              <Route path="units" element={<ProtectedRoute roles={["admin"]}><Units /></ProtectedRoute>} />
              <Route path="users" element={<ProtectedRoute roles={["admin"]}><UsersAdmin /></ProtectedRoute>} />
              <Route path="roles" element={<ProtectedRoute roles={["admin"]}><Roles /></ProtectedRoute>} />
              <Route path="admin/rfq-orphans" element={<ProtectedRoute roles={["admin"]}><RfqOrphans /></ProtectedRoute>} />
              {/* NOTE: must NOT live at /api-keys — the ingress proxies every path
                  beginning with /api to the backend, so a hard refresh would 404. */}
              <Route path="admin/api-keys" element={<ProtectedRoute roles={["admin"]}><ApiKeys /></ProtectedRoute>} />
              <Route path="admin/hr-export" element={<ProtectedRoute roles={["admin"]}><HrExport /></ProtectedRoute>} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Route>
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
